import React, { useContext, useEffect, useRef, useState } from "react";

import { ColorResult } from "react-color";

import { Preview } from "@creatomate/preview";
import { makePost } from "@culturehq/client";
import { Icon, PlainButton } from "@culturehq/components";

import { sleep, t } from "app/utils";

import general from "constants/general";

import { useTemplateBuilder } from "components/admin/templates/context/TemplateBuilderContext";
import UndoButton from "./actions/UndoButton";
import RedoButton from "./actions/RedoButton";
import AspectRatioDropdown from "./actions/AspectRatioDropdown";

import ColorPicker from "../common/ColorPicker";

import getRgbColor from "../utils/getRgbColor";
import { CreatomateContext } from "../contexts/VideoStudioContext";
import getRgbObjFromString from "../utils/getRgbObjFromString";
import getHexFromRgbObject from "../utils/getHexFromRgbObject";
import getHslFromRgba from "../utils/getHslFromRgba";
import DownloadDropdown from "./actions/DownloadDropdown";
import SaveButton from "./actions/SaveButton";
import DownloadButton from "./actions/DownloadButton";
import TemplateActionButtons from "./actions/TemplateActionButtons";
import getCreatomateMediaData from "../utils/creatomate/getCreatomateMediaData";

interface ModalActionsProps {
  onClose: () => void;
  onConfirmClose: () => void;
  open: boolean;
  preview: Preview;
}

const ModalActions: React.FC<ModalActionsProps> = ({
  onClose, onConfirmClose, open, preview
}) => {
  const {
    data,
    handleUpdateData,
    handleAutosaveDraft
  } = useContext(CreatomateContext);
  const {
    burnSubtitles,
    montage,
    fillColor,
    story
  } = data;
  const [backgroundColor, setBackgroundColor] = useState<ColorResult>(general.DEFAULT_COLOR);
  const [isDownloading, setIsDownloading] = useState(false);
  const isCancelled = useRef(false);

  const { templateBuilderSelection } = useTemplateBuilder();

  useEffect(() => () => {
    isCancelled.current = true;
  }, []);

  useEffect(() => {
    if (fillColor) {
      const rgbaColor = getRgbObjFromString(fillColor);
      setBackgroundColor({
        hex: getHexFromRgbObject(rgbaColor),
        rgb: rgbaColor,
        hsl: getHslFromRgba(rgbaColor)
      });
    } else {
      const rgbaColor = getRgbObjFromString("#000000");
      setBackgroundColor({
        hex: getHexFromRgbObject(rgbaColor),
        rgb: rgbaColor,
        hsl: getHslFromRgba(rgbaColor)
      });
    }
  }, [fillColor]);

  const handleBackgroundColorChange = async (color: ColorResult) => {
    setBackgroundColor(color);
    await preview.applyModifications({ fill_color: getRgbColor(color) });
  };

  const setProgressBarAdvance = async () => {
    const { duration } = story?.media || { duration: 0 };
    const renderTime = (duration || 0) * 0.9;
    const totalProgress = 98;
    const increment = totalProgress / renderTime;
    let currentProgress = 0;

    for (let i = 0; i < renderTime; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(100);
      if (isCancelled.current) return;
      currentProgress += increment;
      handleUpdateData({ progress: currentProgress });
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    handleUpdateData({ error: undefined, loading: true, progress: 1 });
    const params: any = {
      storyId: story?.id,
      mediaData: getCreatomateMediaData(preview, burnSubtitles),
      download: true
    };
    if (montage) {
      await makePost("/stories/generate_montage", params);
    } else {
      await makePost("/stories/process_media", params);
    }
    if (isCancelled.current) return;
    await setProgressBarAdvance();
    if (isCancelled.current) return;
    handleUpdateData({ progress: 98 });
    setIsDownloading(false);
  };

  const stateControlButtons = () => {
    if (templateBuilderSelection === "none") {
      return (
        <>
          {!montage && !!story?.media?.subtitles?.length ? (
            <DownloadDropdown onDownload={handleDownload} preview={preview} />
          ) : (
            <DownloadButton
              isDownloading={isDownloading}
              onDownload={handleDownload}
              preview={preview}
            />
          )}
          <SaveButton onClose={onClose} preview={preview} />
        </>
      );
    }

    // if template exist active should be true, otherwise it should be false
    return <TemplateActionButtons preview={preview} onClose={onClose} />;
  };

  return (
    <div className="chq-pan--hd__buttons">
      <div className="Video-Options">
        <UndoButton preview={preview} />
        <RedoButton preview={preview} />
      </div>
      <ColorPicker
        initialColor={backgroundColor}
        onChangeColor={handleBackgroundColorChange}
      />
      <AspectRatioDropdown
        open={open}
        preview={preview}
      />
      {stateControlButtons()}
      <PlainButton className="closemod-btn" aria-label={t("Close")} onClick={onConfirmClose}>
        <Icon icon="close" />
      </PlainButton>
    </div>
  );
};

export default ModalActions;
